interface CompletionEntry {
  userId: string;
  displayName: string;
  firstCompletedAt: number;
  count: number;
  isVerifier: boolean;
}

export type Fetcher = (cursor?: string) => Promise<{
  items: CompletionEntry[];
  nextCursor: string | null;
}>;

export class CompletionListModal {
  private backdrop: HTMLElement | null = null;
  private items: CompletionEntry[] = [];
  private nextCursor: string | null = null;
  private loading = false;
  private fetcher: Fetcher | null = null;

  async show(levelName: string, _remoteId: string, fetcher: Fetcher): Promise<void> {
    this.hide();
    this.fetcher = fetcher;
    this.items = [];
    this.nextCursor = null;

    const backdrop = document.createElement('div');
    backdrop.id = 'clm-backdrop';
    backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:80;display:flex;align-items:center;justify-content:center;padding:20px;font-family:"Courier New",monospace';
    backdrop.innerHTML = `
      <div style="background:#0a0a1a;border:1px solid #00ffff44;border-radius:8px;padding:20px;max-width:380px;width:100%;color:#ccc;max-height:80vh;display:flex;flex-direction:column">
        <div id="clm-header" style="color:#00ffff;font-size:1.1rem;font-weight:bold;margin-bottom:12px;text-shadow:0 0 8px #00ffff66">
          ${escapeHtml(levelName)} — Completions
        </div>
        <div class="clm-list" style="flex:1;overflow-y:auto;display:flex;flex-direction:column;gap:6px;margin-bottom:12px"></div>
        <div style="text-align:center"><button id="clm-loadmore" style="background:#111;color:#00ffff;border:1px solid #00ffff44;padding:6px 18px;font:inherit;font-size:0.75rem;border-radius:4px;cursor:pointer">LOAD MORE</button></div>
        <div style="text-align:center;margin-top:8px"><button id="clm-close" style="background:none;border:none;color:#666;font:inherit;font-size:0.75rem;cursor:pointer">Close</button></div>
      </div>
    `;
    document.body.appendChild(backdrop);
    this.backdrop = backdrop;

    backdrop.addEventListener('pointerdown', (e) => {
      if (e.target === backdrop) this.hide();
    });
    backdrop.querySelector('#clm-close')?.addEventListener('click', () => this.hide());
    backdrop.querySelector('#clm-loadmore')?.addEventListener('click', () => this.fetchPage());

    await this.fetchPage();
  }

  hide(): void {
    if (this.backdrop) { this.backdrop.remove(); this.backdrop = null; }
    this.items = [];
    this.nextCursor = null;
    this.loading = false;
    this.fetcher = null;
  }

  private async fetchPage(): Promise<void> {
    if (!this.fetcher || this.loading) return;
    this.loading = true;
    this.updateLoadMoreButton();
    try {
      const r = await this.fetcher(this.nextCursor ?? undefined);
      this.items = [...this.items, ...r.items];
      this.nextCursor = r.nextCursor;
    } catch {
      // Silent — caller is responsible for surfacing errors via toast.
    } finally {
      this.loading = false;
      this.renderList();
      this.updateLoadMoreButton();
    }
  }

  private renderList(): void {
    if (!this.backdrop) return;
    const list = this.backdrop.querySelector('.clm-list') as HTMLElement;
    if (this.items.length === 0 && !this.loading) {
      list.innerHTML = `<div style="color:#667;text-align:center;padding:16px">No completions yet.</div>`;
      return;
    }
    list.innerHTML = this.items.map((it) => {
      const trophy = it.isVerifier ? '🏆 ' : '';
      const color = it.isVerifier ? '#00ffff' : '#ccc';
      const countChip = it.count > 1
        ? `<span style="background:#222;color:#888;padding:1px 6px;border-radius:8px;font-size:0.65rem;margin-left:6px">×${it.count}</span>`
        : '';
      return `
        <div class="clm-row" style="display:flex;justify-content:space-between;align-items:center;padding:6px 8px;background:rgba(0,255,255,0.03);border-radius:4px">
          <span style="color:${color}">${trophy}${escapeHtml(it.displayName)}${countChip}</span>
          <span style="color:#666;font-size:0.65rem">${relativeTime(it.firstCompletedAt)}</span>
        </div>`;
    }).join('');
  }

  private updateLoadMoreButton(): void {
    if (!this.backdrop) return;
    const btn = this.backdrop.querySelector('#clm-loadmore') as HTMLButtonElement;
    if (this.loading) { btn.textContent = 'LOADING…'; btn.disabled = true; return; }
    if (!this.nextCursor) { btn.textContent = 'NO MORE'; btn.disabled = true; return; }
    btn.textContent = 'LOAD MORE'; btn.disabled = false;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
